import { create } from "zustand";
import { persist } from "zustand/middleware";

interface NavSidebarStore {
  /** Module list rendered as the slim icon strip. */
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

/**
 * Persisted module-list collapse state. The list starts expanded and
 * collapses only when the user clicks the footer toggle; the collapsed strip
 * has no tooltips or aria-labels yet, so defaulting to it would leave every
 * module unnamed. The choice survives reloads; previously it was per-mount
 * React state.
 */
export const useNavSidebarStore = create<NavSidebarStore>()(
  persist(
    (set) => ({
      collapsed: false,
      setCollapsed: (collapsed) => set({ collapsed }),
    }),
    {
      name: "nav-sidebar-store",
      // Persist the flag, never the setter: the default merge spreads whatever
      // was written over the live state, so a stored `setCollapsed: null` from
      // a truncated write would replace the action and every toggle would
      // throw until the user cleared site data. Rehydration also validates the
      // type rather than trusting it, and falls back to the default.
      partialize: (state) => ({ collapsed: state.collapsed }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as { collapsed?: unknown } | undefined;
        return {
          ...currentState,
          collapsed:
            typeof persisted?.collapsed === "boolean"
              ? persisted.collapsed
              : currentState.collapsed,
        };
      },
    },
  ),
);
